import dayjs from "dayjs";
import * as XLSX from "xlsx";

import type { LibroMayorLine } from "~/types/contabilidad";

interface Balance8ColumnasExportData {
  lines: LibroMayorLine[];
  period: string;
  companyName: string;
}

/**
 * Exports the Balance 8 Columnas report to Excel
 */
export const exportBalance8ColumnasToExcel = ({
  lines,
  period,
  companyName,
}: Balance8ColumnasExportData): void => {
  const workbook = XLSX.utils.book_new();

  // Header section data
  const headerData = [
    ["BALANCE 8 COLUMNAS"],
    [],
    ["Empresa:", companyName],
    ["Periodo:", period],
    ["Fecha de generacion:", dayjs().format("DD/MM/YYYY HH:mm")],
    [],
  ];

  // Table header
  const tableHeader = [
    "Codigo",
    "Cuenta",
    "Tipo",
    "Saldo Inicial",
    "Debitos",
    "Creditos",
    "Movimiento Neto",
    "Saldo Final",
  ];

  // Table data
  const tableData = lines.map((line) => {
    const debitos = Number(line.debitos_periodo);
    const creditos = Number(line.creditos_periodo);
    const movimientoNeto = debitos - creditos;

    return [
      line.account_code,
      line.account_name,
      line.account_type,
      Number(line.saldo_inicial),
      debitos,
      creditos,
      movimientoNeto,
      Number(line.saldo_final),
    ];
  });

  // Calculate totals
  const totals = lines.reduce(
    (acc, line) => {
      const debitos = Number(line.debitos_periodo);
      const creditos = Number(line.creditos_periodo);
      return {
        saldo_inicial: acc.saldo_inicial + Number(line.saldo_inicial),
        debitos_periodo: acc.debitos_periodo + debitos,
        creditos_periodo: acc.creditos_periodo + creditos,
        movimiento_neto: acc.movimiento_neto + (debitos - creditos),
        saldo_final: acc.saldo_final + Number(line.saldo_final),
      };
    },
    {
      saldo_inicial: 0,
      debitos_periodo: 0,
      creditos_periodo: 0,
      movimiento_neto: 0,
      saldo_final: 0,
    }
  );

  // Totals row
  const totalsRow = [
    "",
    "",
    "TOTALES",
    totals.saldo_inicial,
    totals.debitos_periodo,
    totals.creditos_periodo,
    totals.movimiento_neto,
    totals.saldo_final,
  ];

  // Combine all data
  const sheetData = [...headerData, tableHeader, ...tableData, totalsRow];

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

  // Set column widths
  worksheet["!cols"] = [
    { wch: 12 }, // Codigo
    { wch: 40 }, // Cuenta
    { wch: 12 }, // Tipo
    { wch: 18 }, // Saldo Inicial
    { wch: 18 }, // Debitos
    { wch: 18 }, // Creditos
    { wch: 18 }, // Movimiento Neto
    { wch: 18 }, // Saldo Final
  ];

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, "Balance 8 Columnas");

  // Generate filename
  const filename = `balance_8_columnas_${dayjs().format("YYYY-MM-DD")}.xlsx`;

  // Download file
  XLSX.writeFile(workbook, filename);
};
